import React, { ReactNode, useEffect } from 'react';
import {
	Form,
	Button,
	Table,
	Row,
	Col,
	Alert,
	InputGroup,
} from 'react-bootstrap';
import { Link, useForm, usePage } from '@inertiajs/react';

import AuthedLayout from '../../Layouts/Authed';
import PcCarrier from '../../Data/PcCarrier';
import Valuation from '../../Data/Valuation';
import PcCarrierRow from './PcCarriers/PcCarrierRow';
import { getEventValue, replaceNullWithEmpty } from './Helpers/RepeatingForms';
import { range } from '../../utils';
import PcCarrierOverridesRow from './PcCarriers/PcCarrierOverridesRow';
import formatDigits from './Helpers/FormatDigits';
import FormHeader from '../../Components/Forms/FormHeader';
import InstructionsPanel from '../../Components/Forms/InstructionsPanel';
import Tooltip from '../../Components/Forms/Tooltip';
import formatCurrency from './Helpers/FormatCurrency';
import AutosaveStatus from '../../Components/Forms/AutosaveStatus';
import MarkComplete from '../../Components/Forms/MarkComplete';
import useValuationStepAutosave from './Helpers/useValuationStepAutosave';

export interface PcCarrierAdditionalInfo {
	overrides: Record<number, number | null>;
}

interface Props {
	valuation: Valuation;
	pcCarriers: (PcCarrier & PcCarrierAdditionalInfo)[];
	maxPcCarriersPerValuation: number;
	currentYear: number;
}

interface FormData {
	annual_total_premium: number;
	annual_total_commission: number;
	pcCarriers: (Partial<PcCarrier> & Partial<PcCarrierAdditionalInfo>)[];
	mark_complete: boolean;
}

const PcCarriers = (props: Props) => {
	const { errors, readOnly } = usePage<any>().props;

	const { data, setData, post, processing } = useForm<FormData>({
		annual_total_premium: props.valuation.annual_total_premium ?? '',
		annual_total_commission: props.valuation.annual_total_commission ?? '',
		pcCarriers: replaceNullWithEmpty(props.pcCarriers) ?? [],
		mark_complete: props.valuation.completed_steps?.includes('pc-carriers') ?? false,
	});
	const autosaveStatus = useValuationStepAutosave('pc-carriers', props.valuation.id, data);

	const submit = (event: any) => {
		event.preventDefault();
		post(`/valuation-form/${props.valuation.id}/pc-carriers`, {
			onError: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
		});
	};

	const updateIndex = (event: React.ChangeEvent<any>, index: number) => {
		const newData: any = { ...data };
		newData.pcCarriers[index][event.target.name] = getEventValue(event);
		setData(newData);
	};

	const updateOverride = (
		index: number,
		year: string,
		event: React.ChangeEvent<any>
	) => {
		const newData: any = { ...data };
		newData.pcCarriers[index].overrides[year] = getEventValue(event);
		setData(newData);
	};

	const setAnnualTotalPremium = (event: React.ChangeEvent<any>) => {
		setData('annual_total_premium', event.target.value);
	};

	const setAnnualTotalCommission = (event: React.ChangeEvent<any>) => {
		setData('annual_total_commission', event.target.value);
	};

	const percentCommissions = data.annual_total_premium > 0
		? ((data.annual_total_commission / data.annual_total_premium) * 100).toFixed(2)
		: 0;

	const summedTotalAnnualPrem = data.pcCarriers.reduce(
		(accumulator, currentValue) =>
			accumulator + (currentValue?.total_annual_premium || 0),
		0
	);

	const summedAnnualAgencyComm = data.pcCarriers.reduce(
		(accumulator, currentValue) =>
			accumulator + (currentValue?.annual_agency_commission || 0),
		0
	);

	const totalPremComparison =
		data.annual_total_premium > 0
			? (
					(summedTotalAnnualPrem / data.annual_total_premium) *
					100
			  ).toFixed(2)
			: 0;

	const totalCommComparison =
		data.annual_total_commission > 0
			? (
					(summedAnnualAgencyComm / data.annual_total_commission) *
					100
			  ).toFixed(2)
			: 0;

	const yearTotal = (year: number) =>
		formatCurrency(
			data.pcCarriers.reduce(
				(accumulator, currentValue) =>
					accumulator + (currentValue?.overrides?.[year] || 0),
				0
			)
		);

	useEffect(() => {
		scrollTo(0,0);
	}, []);

	return (
		<div>
			<FormHeader
				agencyName={props.valuation.agency_name}
				effectiveDate={props.valuation.effective_date}
				pageName='Top 5 P&C Carriers'
			/>
			<InstructionsPanel>
				Use this tab to record your agency's total written premium and commissions (including life and health commissions if applicable), premium and commissions with your top 5 property and casualty carriers.
			</InstructionsPanel>
			<Form onSubmit={submit} className='valuationForm'>
				<fieldset disabled={readOnly}>
				<Row>
					<Col>
						<Form.Group className='py-4' as={InputGroup}>
							<Form.Label
								id='annual_total_premium_label'
								className='w-50 px-2'
							>
								What is the estimated Annual Total Premium
								Dollar amount currently written?
							</Form.Label>
							<InputGroup.Text>$</InputGroup.Text>
							<Form.Control
								id='annual_total_premium'
								aria-labelledby='annual_total_premium_label'
								onBlur={(event) => {
									formatDigits(event, 2);
									setAnnualTotalPremium(event);
								}}
								onChange={setAnnualTotalPremium}
								type='number'
								value={data.annual_total_premium}
							/>
							{errors.annual_total_premium && (
								<Alert variant='dkRed'>
									{errors.annual_total_premium}
								</Alert>
							)}
						</Form.Group>
					</Col>
				</Row>
				<Row>
					<Col>
						<Form.Group className='py-4' as={InputGroup}>
							<Form.Label
								id='annual_total_commission_label'
								className='w-50 px-2'
							>
								What is the estimated Annual Total Commission &
								Fees Dollar amount currently written?
							</Form.Label>
							<InputGroup.Text>$</InputGroup.Text>
							<Form.Control
								id='annual_total_commission'
								aria-labelledby='annual_total_commission_label'
								onChange={setAnnualTotalCommission}
								onBlur={(event) => {
									formatDigits(event, 2);
									setAnnualTotalCommission(event);
								}}
								type='number'
								value={data.annual_total_commission}
							/>
							{errors.annual_total_commission && (
								<Alert variant='dkRed'>
									{errors.annual_total_commission}
								</Alert>
							)}
						</Form.Group>
					</Col>
				</Row>
				<Row>
					<Col>
						<p>
							Percentage of commissions and fees in relation to
							total premiums:{' '}
							<span className='fw-bold'>{percentCommissions}%</span>
						</p>
					</Col>
				</Row>
				<Table striped borderless hover responsive>
					<thead>
						<tr>
							<th
								id='pcCarrierNum'
								className='cell-xs sticky-cell'
							>
								#
							</th>
							<th id='carrier_name'>Carrier Name</th>
							<th id='total_annual_premium'>
								Total Annual Premium
							</th>
							<th id='percent_of_total_agency_premiums'>
								Percent Of Total Agency Premiums
							</th>
							<th id='annual_agency_commission'>
								Annual Agency Commission
							</th>
							<th id='percent_of_total_agency_commissions'>
								Percent Of Total Agency Commissions
							</th>
						</tr>
					</thead>
					<tbody>
						{data.pcCarriers.map((pcCarrier, index) => (
							<PcCarrierRow
								key={`pcCarrier-${index}`}
								pcCarrier={pcCarrier}
								index={index}
								errors={errors}
								updateIndex={updateIndex}
								annual_total_premium={data.annual_total_premium}
								annual_total_commission={
									data.annual_total_commission
								}
							/>
						))}
					</tbody>
					<tfoot>
						<tr>
							<th />
							<th />
							<th>{formatCurrency(summedTotalAnnualPrem)}</th>
							<th>{totalPremComparison}%</th>
							<th>{formatCurrency(summedAnnualAgencyComm)}</th>
							<th>{totalCommComparison}%</th>
						</tr>
					</tfoot>
				</Table>

				<h4 className='mt-2'>
					Contingent Dollars Received by Year
					<Tooltip id='contingent-dollars'>
						Record your contingent dollars by the year they were received, not earned. For example, if you earned $20,000 in profit sharing for the year 2024 and it was paid in 2025, record $20,000 underneath the 2025 column.
					</Tooltip>
				</h4>
				<Table striped borderless hover responsive>
					<thead>
						<tr>
							<th
								id='pcCarrierOverrideNum'
								className='cell-xs sticky-cell'
							>
								#
							</th>
							<th id='carrier_name_override'>Carrier Name</th>
							{range(
								props.currentYear - 5,
								props.currentYear
							).map((year) => (
								<th key={year}>{year}</th>
							))}
						</tr>
					</thead>
					<tbody>
						{data.pcCarriers.map((pcCarrier, index) => (
							<PcCarrierOverridesRow
								key={`pcCarrier-overrides-${index}`}
								currentYear={props.currentYear}
								pcCarrier={pcCarrier}
								index={index}
								errors={errors}
								updateOverride={updateOverride}
							/>
						))}
					</tbody>
					<tfoot>
						<tr>
							<th />
							<th />
							{range(
								props.currentYear - 5,
								props.currentYear
							).map((year) => (
								<th key={year}>{yearTotal(year)}</th>
							))}
						</tr>
					</tfoot>
				</Table>
				</fieldset>

				<div className='d-flex justify-content-between align-items-center'>
					<Button
						as={Link as any}
						href={`/valuation-form/${props.valuation.id}/accounts`}
						variant='outline-primary'
						className='mt-4'
					>
						Back
					</Button>
					<div className='d-flex align-items-center'>
						{!readOnly && <AutosaveStatus status={autosaveStatus} />}
						{!readOnly && (
							<MarkComplete
								checked={data.mark_complete}
								onChange={(checked) => setData('mark_complete', checked)}
								className='mt-4 ms-4'
							/>
						)}
						{readOnly ? (
							<Button
								as={Link as any}
								href={`/valuation-form/${props.valuation.id}/lh-carriers`}
								className='mt-4 ms-4'
							>
								Next
							</Button>
						) : (
							<Button
								type='submit'
								disabled={processing}
								className='mt-4 ms-4'
							>
								Next
							</Button>
						)}
					</div>
				</div>
			</Form>
		</div>
	);
};

PcCarriers.layout = (page: ReactNode) => <AuthedLayout>{page}</AuthedLayout>;

export default PcCarriers;
